import React, { useContext } from 'react';
import styled from 'styled-components';
import { ResponsivePie } from '@nivo/pie';
import { Card, Typography } from '@material-ui/core';
import GroupIcon from '@material-ui/icons/Group';

import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import { UserContext } from '../../../user/User.context';
import {
  useGetUserTrainingStats,
  useGetUserTrainings
} from '../../../user/User.hooks';
import { formatter } from '../TrainingPage';

const StyledCardContent = styled(CardContent)`
  position: relative;
  height: 300px;
  .content {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    right: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: red;
    text-align: center;
    /* preserve chart interactivity */
    pointer-events: none;

    .label {
      font-size: 16px;
      margin-bottom: 64px;
    }
  }
`;

export interface TrainingChartCardProps {
  userId?: number;
  middleText?: string;
}

const TrainingChartCard: React.FC<TrainingChartCardProps> = (
  props: TrainingChartCardProps
) => {
  const { userId, middleText } = props;
  const { user } = useContext(UserContext);
  const { data } = useGetUserTrainingStats(true, userId || user?.id);
  const { userTrainings } = useGetUserTrainings(true, userId || user?.id);

  const trainingStatsPercentage = (count: number) => {
    const percent: number | undefined = userTrainings?.length
      ? (count / userTrainings.length) * 100
      : undefined;
    return typeof percent === 'number' && isFinite(percent)
      ? formatter.format(percent) + '%'
      : 'N/A';
  };

  let trainingStats;

  if (data) {
    trainingStats = [
      {
        id: `Completed (${trainingStatsPercentage(data.completedCount)})`,
        label: 'Completed',
        value: data.completedCount,
        color: '#61cdbb'
      },
      {
        id: `To-do (${trainingStatsPercentage(data.toDoCount)})`,
        label: 'To-do',
        value: data.toDoCount,
        color: '#f47560'
      },
      {
        id: `In Progress (${trainingStatsPercentage(data.inProgressCount)})`,
        label: 'In Progress',
        value: data.inProgressCount,
        color: '#F1E25B'
      }
    ];
  }

  return (
    <Card>
      <CardTitle>
        <GroupIcon />
        Training Progress
      </CardTitle>
      <StyledCardContent>
        {trainingStats && (
          <ResponsivePie
            data={trainingStats}
            margin={{ top: 50, right: 60, bottom: 80, left: 60 }}
            innerRadius={0.5}
            activeOuterRadiusOffset={8}
            padAngle={0.7}
            cornerRadius={3}
            colors={['#61cdbb', '#f47560', 'rgb(241, 225, 91)']}
            borderWidth={1}
            borderColor={{ from: 'color', modifiers: [['darker', 0]] }}
            arcLabelsSkipAngle={10}
            arcLinkLabelsOffset={6}
            arcLabelsTextColor='#333333'
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsThickness={1}
            arcLinkLabelsColor={{ from: 'color' }}
            arcLinkLabelsTextColor='#333333'
            animate={true}
            legends={[
              {
                anchor: 'bottom',
                direction: 'row',
                translateY: 56,
                itemsSpacing: 20,
                itemWidth: 100,
                itemHeight: 18,
                itemTextColor: '#999',
                symbolSize: 18,
                symbolShape: 'circle',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemTextColor: '#000'
                    }
                  }
                ]
              }
            ]}
          />
        )}
        {middleText && (
          <div className='content'>
            <Typography variant='body1' className='label' color='textSecondary'>
              {middleText}
            </Typography>
          </div>
        )}
      </StyledCardContent>
    </Card>
  );
};

export default TrainingChartCard;
